import { useState } from "react";
import { Copy, Trash2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";

import {
  useDuplicateCandidate,
  useRemoveCandidate,
  useUpsertCandidate,
} from "@/application/use-cases/candidates/use-candidates.queries";
import {
  CandidateFormBody,
  type CandidateFormValues,
} from "@/presentation/components/candidates/candidate-form-body";
import type { CandidateRow } from "@/infrastructure/supabase/database.types";

interface EditCandidateMetaDialogProps {
  candidate: CandidateRow | null;
  onClose: () => void;
  onDeleted?: () => void;
  onDuplicated?: (newSlug: string) => void;
}

export function EditCandidateMetaDialog({
  candidate,
  onClose,
  onDeleted,
  onDuplicated,
}: EditCandidateMetaDialogProps) {
  return (
    <Dialog open={candidate !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!max-w-2xl">
        {candidate ? (
          <Body
            candidate={candidate}
            onClose={onClose}
            onDeleted={onDeleted}
            onDuplicated={onDuplicated}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Body({
  candidate,
  onClose,
  onDeleted,
  onDuplicated,
}: {
  candidate: CandidateRow;
  onClose: () => void;
  onDeleted?: () => void;
  onDuplicated?: (newSlug: string) => void;
}) {
  const [values, setValues] = useState<CandidateFormValues | null>(null);
  const upsert = useUpsertCandidate();
  const remove = useRemoveCandidate();
  const duplicate = useDuplicateCandidate();

  const handleSave = () => {
    if (!values) {
      onClose();
      return;
    }
    upsert.mutate({ id: candidate.id, ...values }, { onSuccess: () => onClose() });
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete candidate "${candidate.name}" and all its component choices?`)) {
      return;
    }
    remove.mutate(candidate.id, {
      onSuccess: () => {
        onClose();
        onDeleted?.();
      },
    });
  };

  const handleDuplicate = () => {
    const defaultName = `${candidate.name} (copy)`;
    const newName = window.prompt("Name for the duplicated candidate:", defaultName);
    if (newName === null || newName.trim().length === 0) return;

    const defaultSlug = `${candidate.slug}-copy`;
    const newSlug = window.prompt("URL slug for the duplicate (lowercase, hyphens):", defaultSlug);
    if (newSlug === null) return;
    const sanitized = newSlug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
    if (sanitized.length === 0) return;

    duplicate.mutate(
      { sourceId: candidate.id, newSlug: sanitized, newName: newName.trim() },
      {
        onSuccess: () => {
          onClose();
          onDuplicated?.(sanitized);
        },
        onError: (err) => {
          window.alert(`Duplicate failed: ${(err as Error).message}`);
        },
      }
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit candidate</DialogTitle>
      </DialogHeader>
      <CandidateFormBody initial={candidate} onChange={setValues} />
      <DialogFooter className="!justify-between">
        <div className="flex gap-2">
          <Button variant="ghost" onClick={handleDelete} className="text-destructive">
            <Trash2 className="size-3.5" />
            Delete
          </Button>
          <Button variant="ghost" onClick={handleDuplicate} disabled={duplicate.isPending}>
            <Copy className="size-3.5" />
            {duplicate.isPending ? "Duplicating…" : "Duplicate"}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
